import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

const money = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const percent = value => `${Number(value || 0).toFixed(2)}%`;

const todayBR = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const firstDayMonthBR = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
};

const labelStyle = {
  color: "#64748b",
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 8
};

const valueStyle = {
  color: "#0f172a",
  fontSize: 26,
  fontWeight: 800,
  lineHeight: "32px"
};

const ConversionDashboard = () => {
  const [initialDate, setInitialDate] = useState(firstDayMonthBR());
  const [finalDate, setFinalDate] = useState(todayBR());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    cards: {},
    sellerRanking: [],
    lostReasons: []
  });

  const cards = data.cards || {};
  const sellerRanking = data.sellerRanking || [];
  const lostReasons = data.lostReasons || [];

  const loadData = async () => {
    try {
      setLoading(true);

      const response = await api.get(
        `/dashboard/conversion?initialDate=${initialDate}&finalDate=${finalDate}`
      );

      setData(response.data || {});
    } catch (error) {
      console.error("Erro ao carregar dashboard de conversão:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const topSeller = useMemo(() => {
    if (!sellerRanking.length) return null;
    return sellerRanking[0];
  }, [sellerRanking]);

  return (
    <div style={{ marginTop: 24, marginBottom: 32, gridColumn: "1 / -1", width: "100%" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #111827 0%, #b91c1c 55%, #ef4444 100%)",
          borderRadius: 24,
          padding: 24,
          color: "#ffffff",
          boxShadow: "0 20px 45px rgba(239, 68, 68, 0.22)",
          marginBottom: 18
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 600 }}>
              Painel Comercial
            </div>
            <h2 style={{ margin: "6px 0 4px", fontSize: 28, fontWeight: 900 }}>
              Performance de Conversão
            </h2>
            <div style={{ opacity: 0.85 }}>
              Acompanhe vendas, conversão, ticket médio, leads parados e ranking dos vendedores.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Data inicial</div>
              <input
                type="date"
                value={initialDate}
                onChange={e => setInitialDate(e.target.value)}
                style={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 12px"
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Data final</div>
              <input
                type="date"
                value={finalDate}
                onChange={e => setFinalDate(e.target.value)}
                style={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 12px"
                }}
              />
            </div>

            <button
              onClick={loadData}
              disabled={loading}
              style={{
                border: 0,
                borderRadius: 12,
                padding: "11px 18px",
                fontWeight: 800,
                cursor: "pointer",
                background: "#ffffff",
                color: "#b91c1c"
              }}
            >
              {loading ? "Carregando..." : "Filtrar"}
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 14
        }}
      >
        <div style={cardStyle}>
          <div style={labelStyle}>Valor vendido</div>
          <div style={{ ...valueStyle, color: "#16a34a" }}>{money(cards.salesAmount)}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Vendas realizadas</div>
          <div style={valueStyle}>{cards.salesCount || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Taxa de conversão</div>
          <div style={{ ...valueStyle, color: "#dc2626" }}>{percent(cards.conversionRate)}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Ticket médio</div>
          <div style={valueStyle}>{money(cards.averageTicket)}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Leads no período</div>
          <div style={valueStyle}>{cards.leadsTotal || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Leads em aberto</div>
          <div style={{ ...valueStyle, color: "#f59e0b" }}>{cards.openTickets || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Leads parados +30min</div>
          <div style={{ ...valueStyle, color: "#dc2626" }}>{cards.stoppedTickets || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Vendas perdidas</div>
          <div style={{ ...valueStyle, color: "#ef4444" }}>{cards.lostCount || 0}</div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 16,
          marginTop: 16
        }}
      >
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={labelStyle}>Ranking de vendedores</div>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                Quem mais está convertendo no período
              </div>
            </div>

            {topSeller && (
              <div
                style={{
                  background: "#fef2f2",
                  color: "#b91c1c",
                  borderRadius: 999,
                  padding: "8px 12px",
                  fontWeight: 800,
                  fontSize: 12
                }}
              >
                Destaque: {topSeller.name}
              </div>
            )}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left", fontSize: 12 }}>
                  <th style={{ padding: "10px 8px" }}>#</th>
                  <th style={{ padding: "10px 8px" }}>Vendedor</th>
                  <th style={{ padding: "10px 8px" }}>Leads</th>
                  <th style={{ padding: "10px 8px" }}>Vendas</th>
                  <th style={{ padding: "10px 8px" }}>Valor</th>
                  <th style={{ padding: "10px 8px" }}>Conversão</th>
                </tr>
              </thead>

              <tbody>
                {sellerRanking.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: 16, color: "#64748b" }}>
                      Nenhum dado encontrado para o período.
                    </td>
                  </tr>
                ) : (
                  sellerRanking.map((seller, index) => (
                    <tr key={seller.userId || index} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "12px 8px", fontWeight: 900 }}>
                        {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : index + 1}
                      </td>
                      <td style={{ padding: "12px 8px", fontWeight: 800, color: "#0f172a" }}>
                        {seller.name}
                      </td>
                      <td style={{ padding: "12px 8px" }}>{seller.tickets || 0}</td>
                      <td style={{ padding: "12px 8px" }}>{seller.sales || 0}</td>
                      <td style={{ padding: "12px 8px", color: "#16a34a", fontWeight: 800 }}>
                        {money(seller.salesAmount)}
                      </td>
                      <td style={{ padding: "12px 8px", color: "#dc2626", fontWeight: 800 }}>
                        {percent(seller.conversionRate)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Motivos de não venda</div>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 12 }}>
            Principais perdas
          </div>

          {lostReasons.length === 0 ? (
            <div style={{ color: "#64748b" }}>Nenhum motivo registrado.</div>
          ) : (
            lostReasons.map((item, index) => (
              <div key={`${item.reason}-${index}`} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#334155", fontWeight: 700 }}>{item.reason}</span>
                  <span style={{ color: "#ef4444", fontWeight: 900 }}>{item.total}</span>
                </div>

                <div
                  style={{
                    height: 8,
                    borderRadius: 999,
                    background: "#fee2e2",
                    marginTop: 6,
                    overflow: "hidden"
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Number(item.total || 0) * 10)}%`,
                      height: "100%",
                      background: "#ef4444"
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversionDashboard;
